/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

import { EventEmitter, Injectable, Injector } from '@angular/core';

import {
  timer,
  Observable,
  Subscription,
  BehaviorSubject,
  Subject,
} from 'rxjs';

import { LoggingService } from '../../../logging/logic/logging.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { CommunicationService, ServerRequest } from '../../../communication/logic/commumication.service';
import { AuthenticationService } from '../../../authentication/authentication.service';
import { RemoteActionService } from '../remote-action.service';
import { RemoteActionDaoService } from '../../dao/remote-action-dao.service';
import { TransmittedRemoteActionDaoService } from '../../dao/transmitted-remote-action-dao.service';

import { TranslateService } from '@ngx-translate/core';
import {
  FileManagerService,
  FOLDER_DATAFILES,
} from 'src/app/_services/file-manager/file-manager.service';
import { BadgesService } from 'src/app/_services/badges/logic/badges.service';
import { ErrorService } from 'src/app/_services/error/error.service';
import { IMSAction } from 'src/app/_model/menu';

import { Device } from '@capacitor/device';
import { App, AppState } from '@capacitor/app';
import { LBSBackgroundFetch } from 'plugins/lbs-background-fetch/dist/esm';

import { Deferred } from 'ts-deferred';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { Mutex } from 'async-mutex';
import { PreferencesService } from 'src/app/_services/preferences/preferences.service';
import {
  LocalNotifications,
  ScheduleOptions,
} from '@capacitor/local-notifications';
import { LBSUtils } from 'src/app/_util/lbs-utils';
import { Capacitor } from '@capacitor/core';
import { EventService, IMSEventContentTypeHook, IMSEventMessageType, IMSEventSourceType, IMSInternalEventHook } from 'src/app/_services/event/event.service';
import { AppParameter } from 'src/app/_model/app-parameter';
import { DynFormsService } from 'src/app/_services/dyn-forms/logic/dyn-forms.service';

// Defined times for the lifecycle of the cached fifo execution
const TIMES = {
  // Time actions get sents after adding
  SOON: 1000 * 5, // 5 seconds
  // Delay time after an error happened
  RETRY: 1000 * 15, // 15 seconds
  // Maximum delaytime
  MAX_INTERVAL: 1000 * 60 * 60, // 1 hour
};

/**
 * A RemoteActionService implementation which considers the FIFO and cachable flag of [[RemoteAction]]s
 *
 * After adding a Remote Action a short timer will run, after that all cached RAs will be sent to the server
 * In case an transmission fails, the cache will still iterate over the remaining RAs and try to send all
 * not FIFO RAs.
 * After the retry timer expires the complete cached RA queue will be executed again
 *
 * In case of a not cachable RA passed to send there are to cases:
 *  - It is FIFO: It will try to send all RAs from the cache and then send the not cachable one.
 *    If an error happens in either clearing the cache or the RA itself, the caller will be notified
 *  - It is not FIFO: It will try to send the RA immediatally.
 *    If an error happens during sending the RA, the caller will be notified.
 */
@Injectable()
export class CachedFifoRemoteActionService implements RemoteActionService {
  // Public Member
  public actionEvent$ = new Subject<IMSAction>();
  public remoteActions$ = new BehaviorSubject<RemoteAction[]>(undefined);
  public transmittedRemoteActions$ = new BehaviorSubject<RemoteAction[]>(
    undefined,
  );
  public isTransmitting$ = new BehaviorSubject<boolean>(false);
  public lastTransmissionError$ = new BehaviorSubject<string | undefined>(undefined);
  public appParams: AppParameter;
  public get maxTransmission(): number{ 
    return this.appParams?.['imsmc.remoteaction.maxtransmission'] ?? 15;
  }
  public get enableLocalNotification(): boolean {
    return this.appParams?.['imsmc.service.remoteactionservice.notificationonfailure.enable'] ?? true;
  }
  public get minimumAge(): number {
    return this.appParams?.['imsmc.service.remoteactionservice.notificationonfailure.minimumage'] ?? 5 * 60 * 1000;
  }

  public get holdBackFilesDays(): number {
    return this.appParams?.['imsmc.remoteaction.holdbackfilesdays'] ?? 30;
  }

  public get rescanOnWarmStart(): boolean {
    return this.appParams?.getBooleanParameter('imsmc.service.remoteactionservice.rescanonwarmstart', true) ?? false;
  }

  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Private Member
  private subscription: Subscription;
  private timer$: Observable<number>;
  private isRetrying = false;
  private retryMultiplier = 1;
  private background = false;

  private waitForUI: Deferred<boolean> = undefined;

  private transmitMutex = new Mutex();

  private executeActionFn: (action: IMSAction) => Promise<
    | boolean
    | void
    | {
        [key: string]: any;
      }
  >;

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private commService: CommunicationService,
    private authenticationService: AuthenticationService,
    private remoteActionDaoService: RemoteActionDaoService,
    private transmittedRemoteActionDaoService: TransmittedRemoteActionDaoService,
    private translate: TranslateService,
    private errorService: ErrorService,
    private fileManager: FileManagerService,
    private badgeService: BadgesService,
    private preferencesService: PreferencesService,
    private eventService: EventService, 
    private injector: Injector,
  ) {
    this.initialize();

    authenticationService.authenticationState.subscribe({
      next: (authenticated) => {
        if (authenticated) {
          this.scanForRemoteActions(TIMES.SOON);
          this.logger.log(
            this.constructor.name,
            '- Begin scanning for Remote actions soon',
          );
        } else {
          if (this.subscription) {
            this.subscription.unsubscribe();
            this.logger.log(
              this.constructor.name,
              '- Disable Timer when user is logged out',
            );
          }
        }
      },
    });

    if (Capacitor.isNativePlatform()) {
      LBSBackgroundFetch.addListener('ENTERBACKGROUND', async () => {
        this.logger.log(
          this.constructor.name,
          'JBackground - Plugin ENTERBACKGROUND event received - the app is allowed to process something in the background for about 30 seconds',
        );

        await this.transmitRemoteActionBatchFromBackground(() => {
          return LBSBackgroundFetch.finishedEnterBackgroundRemoteActionBatchTransmission(
            {
              remainingRemoteActions: this.remoteActions$.getValue()?.length ?? 0,
            },
          );
        });
      });
      console.log(this.constructor.name, 'JBPWAPlugin register BACKGROUNDEXECUTION listener');
      LBSBackgroundFetch.addListener('BACKGROUNDEXECUTION', async () => {
        this.logger.log(
          this.constructor.name,
          'JBPWAPlugin JBackground - Plugin BACKGROUNDEXECUTION event received - the app is allowed to process something in the background until iOS cancels the task',
        );

        await this.transmitRemoteActionBatchFromBackground(() => {
          return LBSBackgroundFetch.finishedBackgroundProcessingRemoteActionBatchTransmission(
            {
              remainingRemoteActions: this.remoteActions$.getValue()?.length ?? 0,
            },
          );
        });
      });
    }

    App.addListener('appStateChange', (state: AppState) => {
      if (state.isActive) {
        this.updateObservable();
        this.background = false;

        if (this.rescanOnWarmStart && this.remoteActions$.getValue()?.length > 0) {
          this.logger.log(this.constructor.name, `app entered foreground and has ${this.remoteActions$.getValue()?.length} cached remote actions - retriggering scan for remote actions`)
          this.scanForRemoteActions(TIMES.SOON)
        }
      } else {
        this.background = true;
      }
    });
  }

  public delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadRemoteActions();
      const storedError = await this.preferencesService.getLastTransmissionError();
      if (storedError) {
        this.lastTransmissionError$.next(storedError);
      }
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // RemoteActionService
  public async sendRemoteAction(remoteAction: RemoteAction): Promise<any> {
    let request: ServerRequest;
    let response;
    if (remoteAction.cachable) {
      // JB20190326: Cachable remote actions are appended to the cache and a job to send is started soon
      this.logger.log(
        this.constructor.name,
        '- Saving remote action',
        remoteAction.endpoint,
      );
      // APP4-948: actions carrying a dedup key (cloud autosaves) replace a still pending action
      // with the same key instead of stacking up — only the newest snapshot is transmitted and no
      // duplicate AC is created while the first autosave has not yet returned an acid.
      const dedupKey = remoteAction.data?.['pi_dedupkey'];
      if (dedupKey) {
        const queuedActions = await this.remoteActionDaoService.getRemoteActions();
        for (const queued of queuedActions.filter(
          (qa) => !qa.sent && qa.data?.['pi_dedupkey'] === dedupKey,
        )) {
          this.logger.log(
            this.constructor.name,
            '- Replacing pending remote action with same dedup key',
            dedupKey,
            queued.id,
          );
          await this.remoteActionDaoService.removeRemoteAction(queued);
        }
      }
      await this.remoteActionDaoService.addRemoteAction(remoteAction);
      await this.updateObservable();
      this.scanForRemoteActions(TIMES.SOON);
    } else {
      // JB20190326: Non cachable remote action. If FIFO relevant, need to flush the whole cache and then execute the remote action blocking
      try {
        if (remoteAction.fifo) {
          this.logger.log(
            this.constructor.name,
            '- Not cachable, but fifo relevant. Flushing cached remote actions',
          );

          await this.processRemoteActions();

          this.logger.log(
            this.constructor.name,
            '- Flushed all cached remote actions',
          );
        } else {
          this.logger.log(
            this.constructor.name,
            '- Not cachable, but not fifo relevant',
          );
        }

        this.logger.log(
          this.constructor.name,
          '- Send non cachable remote action',
          remoteAction.endpoint,
        );
        await this.resolveLateBoundAcIdentity(remoteAction);
        request = await this.commService.getServerRequest(remoteAction);
        console.log('RequestDebug', request);
        this.logger.log(
          this.constructor.name,
          '- Execute instant Remote actions with comm service',
          remoteAction.endpoint,
        );
        response = await this.commService.sendRequest(
          request.url,
          request.headers,
          request.body,
          request.httpMethod,
          remoteAction.forceDisableTimeout
            ? false
            : remoteAction.customTimeoutMs ?? true,
          request.filePath,
        );

        this.logger.log(
          this.constructor.name,
          '- Finished sending non cachable remote action for',
          remoteAction.endpoint,
        );

        await this.executeAfterSendActions(remoteAction, response);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          '- Error while flushing cache and sending non cachable remote action',
          error?.message,
        );
        throw error;
      }

      try {
        await this.addToDeleteFiles(
          request.filesToDelete,
          remoteAction.pi_delayFilesDeletion,
        );
      } catch (error) {
        this.logger.warn(
          this.constructor.name,
          'could not delete files from request',
          request?.filesToDelete,
          'does not influence performance - surpress warning',
        );
      }
      return response;
    }
  }

  public async retryTransmittingRemoteActions(): Promise<void> {
    // try {
    this.logger.log('External Component triggered a retry of transmitting.');
    await this.triggerTransmission();
    // } catch (error) {
    //   this.logger.error(
    //     'Error retrying to send remote actions:',
    //     error?.message,
    //   );

    //   let message = await this.translate
    //     .get('COMMON.ERROR.SUBHEADERS.RETRYRA')
    //     .toPromise();

    //   this.errorService.showErrorAlert(message + error?.message, error);
    // }
    // return;
  }

  public setExecuteActionFunction(
    executeActionFn: (action: IMSAction) => Promise<
      | boolean
      | void
      | {
          [key: string]: any;
        }
    >,
  ) {
    this.executeActionFn = executeActionFn;
  }

  public async reset() {
    await this.remoteActionDaoService.reset();
    await this.transmittedRemoteActionDaoService.reset();
    await this.updateObservable();
    this.badgeService.setCachedRemoteActionsBadge(undefined);
    this.lastTransmissionError$.next(undefined);
    await this.preferencesService.setLastTransmissionError(undefined);
  }

  public async clear() {
    await this.remoteActionDaoService.reset();
    await this.transmittedRemoteActionDaoService.reset();
    await this.updateObservable();
    this.badgeService.setCachedRemoteActionsBadge(undefined);
    this.lastTransmissionError$.next(undefined);
    await this.preferencesService.setLastTransmissionError(undefined);
  }

  public async removeLocalRemoteAction(remoteAction: RemoteAction) {
    await this.remoteActionDaoService.removeRemoteAction(remoteAction);
    await this.updateObservable();
  }
  // Private Functions

  private async loadRemoteActions() {
    try {
      this.logger.log(
        this.constructor.name,
        '- Initializing remote action service',
      );

      await this.transmittedRemoteActionDaoService.purgeRemoteActions();
      await this.updateObservable();
      this.logger.log(
        this.constructor.name,
        '- Initialized remote action observables',
      );
      if (this.authenticationService.authenticationState.value) {
        this.scanForRemoteActions(TIMES.SOON);
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local remote action: ',
        error.message,
      );
      throw error;
    }
  }

  private async addToDeleteFiles(
    filesToDelete: string[],
    delayFilesDeletion: boolean,
  ) {
    if (delayFilesDeletion) {
      await this.preferencesService.addDelayedFilesToDelete(filesToDelete);
    } else {
      await this.preferencesService.addFilesToDelete(filesToDelete);
    }
  }

  private async deleteFiles(filesToDelete: string[]) {
    for (const file of filesToDelete) {
      try {
        const group = file.split('/')[0];
        if (group === 'formscreenshots') {
          await this.fileManager.deleteFile(file);
        } else if (group === FOLDER_DATAFILES) {
          await this.fileManager.deleteDataFile(file);
        }
      } catch (e) {
        this.logger.warn(
          this.constructor.name,
          'Could not delete file',
          file,
          'skip as it is only a cleanup call',
        );
      }
    }
  }

  private async updateObservable(): Promise<void> {
    await this.remoteActionDaoService
      .getRemoteActions()
      .then((remoteActions) => {
        this.remoteActions$.next(remoteActions);
        this.logger.log(this.constructor.name, '- Updated to transmit remote actions observable', remoteActions?.length);
      });

    await this.transmittedRemoteActionDaoService
      .getRemoteActions()
      .then((remoteActions) => {
        this.transmittedRemoteActions$.next(remoteActions);
        this.logger.log(this.constructor.name, '- Updated transmitted remote actions observable', remoteActions?.length);
      });
  }

  /**
   * APP4-948: fills the chain identity (acid/acukey) of a queued dynform upload in right
   * before it goes out.
   *
   * A document only learns its identity from the answer to its FIRST upload
   * (updateACInfo writes it onto the dynformdata row), so everything queued in the
   * meantime was built without one - and an upload without an acid makes the backend
   * allocate a NEW number range value. That is what turned "leave the form (autosave),
   * open it again and submit right away" into two documents: 018 was submitted while the
   * autosaved 017 stayed an open draft forever - nobody released its draft ownership, no
   * draft_finalized push went out, and the other devices kept offering the orphan.
   *
   * Safe to do here because the queue is FIFO: the previous upload of the same document
   * has been answered by the time this runs. Only gaps are filled - an action that carries
   * an acid keeps it, so the optimistic-locking check still compares against the version
   * the form was actually based on.
   */
  private async resolveLateBoundAcIdentity(
    remoteAction: RemoteAction,
  ): Promise<void> {
    const formDataId = remoteAction.data?.['pi_acformdataid'];
    if (formDataId == undefined) {
      return;
    }
    if (
      remoteAction.data['acid'] != undefined ||
      remoteAction.data['acukey'] != undefined
    ) {
      return;
    }

    try {
      const stored = await this.injector
        .get(DynFormsService)
        .getAcIdentity(+formDataId);
      if (stored.acid == undefined && stored.acukey == undefined) {
        return;
      }

      this.logger.log(
        this.constructor.name,
        '- resolveLateBoundAcIdentity: upload of form data',
        formDataId,
        'was queued without a chain identity - sending it with acid',
        stored.acid,
        'acukey',
        stored.acukey,
      );
      remoteAction.data['acid'] = stored.acid;
      remoteAction.data['acukey'] = stored.acukey;
    } catch (error) {
      // The upload still goes out, it just cannot be linked to its chain
      this.logger.warn(
        this.constructor.name,
        '- resolveLateBoundAcIdentity failed for form data',
        formDataId,
        error?.message,
      );
    }
  }

  private async processRemoteActions(): Promise<void> {
    // JB20190408: No need to process Remote Actions if user is not authenticated
    if (!(await this.authenticationService.isAuthenticated())) {
      this.logger.log(
        this.constructor.name,
        '- Did not start processing remote actions, as user is not authenticated',
      );
      return;
    }

    this.logger.log(
      this.constructor.name,
      'start transmitting remote actions exclusive as soon as the transmit mutex is available; is mutex locked=',
      this.transmitMutex.isLocked(),
    );
    try {
      let didTransmit: boolean = false;
      await this.transmitMutex.runExclusive(async () => {
        didTransmit = await this.transmit();
      });
      this.logger.log(
        this.constructor.name,
        'finished transmitting and releases mutex; is mutex locked=',
        this.transmitMutex.isLocked(),
      );
      if (didTransmit) {
         const eventContent: IMSEventContentTypeHook = {
          kind: IMSEventMessageType.HOOK,
          hook: IMSInternalEventHook.RARTRANSMISSION
        }
        this.eventService.sendEvent({
          source: {
            type: IMSEventSourceType.INTERNAL,
            contentType: eventContent
          }
        }, this.appParams);
      }
    } catch (e) {
      this.logger.log(
        this.constructor.name,
        'error while transmitting, but releases mutex for next iteration; is mutex locked=',
        this.transmitMutex.isLocked(),
      );
      throw e;
    }
  }

  private async transmit(): Promise<boolean> {
    this.isTransmitting$.next(true);
    let didTransmit = false;
    try {
      this.logger.log(this.constructor.name, '- Start Processing');
      let happenedError: Error = undefined;

      // JB20200612: Only transmit 15 remote actions at once due to iOS killing the app
      // when to many remote actions are transmitted at once.
      let remoteActionTransmitCount = 0;
      let remoteAction: RemoteAction =
        await this.remoteActionDaoService.getNextRemoteAction(
          happenedError !== undefined,
        );
      while (remoteAction) {
        didTransmit = true;
        if (remoteActionTransmitCount >= this.maxTransmission) {
          this.logger.log(
            this.constructor.name,
            '- Abort processing remote actions after 15 transmitted remote actions - reset timer',
          );
          // JB20200612: After a sucessfull processing the remote actions
          // the timer to scan for remaining remote actions need to be triggered.
          // In case the transmission was aborted due to the max 15 remote actions
          // at once policy.
          this.scanForRemoteActions(TIMES.SOON);
          break;
        }
        this.logger.log(
          this.constructor.name,
          '- execute new remote action check if user is authenticated',
          await this.authenticationService.isAuthenticated(),
        );
        if (!(await this.authenticationService.isAuthenticated())) {
          this.logger.log(
            this.constructor.name,
            '- Abort processing remote actions because user is not authenticated any more',
          );
          break;
        }

        try {
          this.logger.log(
            this.constructor.name,
            '- executing remote action',
            remoteAction.endpoint,
          );

          await this.resolveLateBoundAcIdentity(remoteAction);

          let request = await this.commService.getServerRequest(remoteAction);

          var promise: Promise<any>;

          this.logger.log(
            this.constructor.name,
            '- Execute Remote actions with communication service',
          );

          try {
            promise = await this.commService.sendRequest(
              request.url,
              request.headers,
              request.body,
              request.httpMethod,
              false,
              request.filePath
            );
            let response = await promise;

            this.logger.log(
              this.constructor.name,
              '- Remote action response:',
              response,
            );
            if (response) {
              if (response.data) {
                this.logger.log(
                  this.constructor.name,
                  '- Found a response data:',
                  response.data,
                );
                this.actionEvent$.next({
                  type: 'action',
                  target: response.data,
                });
              } else if (response.action) {
                this.logger.log(
                  this.constructor.name,
                  '- Found a response action:',
                  response.action,
                );
                this.actionEvent$.next({
                  type: 'action',
                  target: response.action,
                });
              }
            }

            this.logger.log(
              this.constructor.name,
              '- remote action executed',
              remoteAction.endpoint,
            );

            // JB20190408: Only save remote action to the 'tagesübersicht' if they are cachable
            if (remoteAction.cachable) {
              remoteAction.executionTimestamp = new Date().getTime();
              remoteAction = this.postSubmitExecution(remoteAction);
              await this.transmittedRemoteActionDaoService.addRemoteAction(
                remoteAction,
              );
            }

            await this.remoteActionDaoService.removeRemoteAction(remoteAction);
            await this.updateObservable();

            try {
              await this.addToDeleteFiles(
                request.filesToDelete,
                remoteAction.pi_delayFilesDeletion,
              );
            } catch (error) {
              this.logger.warn(
                this.constructor.name,
                'could not delete files from request',
                request?.filesToDelete,
                'does not influence performance - surpress warning',
              );
            }

            await this.executeAfterSendActions(remoteAction, response);

            // JB20190326: Reset retry multiplier after at least one successful execution
            this.retryMultiplier = 1;
            this.isRetrying = false;
            remoteActionTransmitCount++;
          } catch (error) {
            happenedError = error;
            remoteActionTransmitCount++;
            this.logger.error(
              this.constructor.name,
              '- error while executing remote action inner',
              error,
            );
          }
        } catch (error) {
          this.logger.error(
            this.constructor.name,
            '- error while executing remote action outer',
            error,
          );
          remoteActionTransmitCount++;
          if (remoteAction.fifo) {
            happenedError = error;
            this.logger.warn(
              this.constructor.name,
              '- trying to execute not fifo marked remote actions in the queue',
            );
          } else {
            this.logger.log(
              this.constructor.name,
              '- Failed remote action was not fifo relevant. Continue to process as normal',
            );
          }
        }

        if (happenedError != undefined) {
          console.log(
            'JBAuthError an error happened:',
            happenedError?.name,
            happenedError?.message,
          );
        }

        remoteAction = await this.remoteActionDaoService.getNextRemoteAction(
          happenedError != undefined,
        );
      }

      this.logger.log(this.constructor.name, '- Finished Processing');

      if (happenedError) {
        throw happenedError;
      }
      this.badgeService.setCachedRemoteActionsBadge(undefined);
      this.lastTransmissionError$.next(undefined);
      await this.preferencesService.setLastTransmissionError(undefined);
    } catch (error) {
      const errorMessage = error?.error?.message ?? error?.message ?? (typeof error === 'string' ? error : JSON.stringify(error));
      this.logger.error(
        this.constructor.name,
        '- Error processing remote actions.',
        errorMessage,
      );
      this.lastTransmissionError$.next(errorMessage);
      await this.preferencesService.setLastTransmissionError(errorMessage);
      const remainingRemoteActions = this.remoteActions$.getValue();
      const amountOfRemainingRar = remainingRemoteActions.length;
      this.badgeService.setCachedRemoteActionsBadge(amountOfRemainingRar);

      this.logger.log(
        this.constructor.name,
        'Show Transmission Error Local Noticiation? enabledLocalNotification:',
        this.enableLocalNotification,
        'amountOfRemainingRar:',
        amountOfRemainingRar,
      );
      if (this.enableLocalNotification && amountOfRemainingRar > 0) {
        const curTs = new Date().getTime();
        this.logger.log(
          this.constructor.name,
          'Show Transmission Error Local Noticiation - age oldest remoteaction:',
          curTs - remainingRemoteActions[0]?.rawRequestTimestamp,
          'minimumAge:',
          this.minimumAge,
        );
        if (
          this.minimumAge <
          curTs - remainingRemoteActions[0]?.rawRequestTimestamp
        ) {
          await this.sendLocalNotificationForRemainingRar(amountOfRemainingRar);
        }
      }

      throw error;
    } finally {
      const remainingRemoteActions =
        await this.remoteActionDaoService.getRemoteActions();
      if (remainingRemoteActions.length === 0) {
        console.log(
          'JBFiles no more remaining remote actions stores, delete files',
        );
        const filesToDelete = await this.preferencesService.getFilesToDelete();
        const delayedFilesToDelete =
          await this.preferencesService.getDelayedFilesToDelete(
            LBSUtils.daysToMilliseconds(this.holdBackFilesDays),
          );
        filesToDelete.push(...delayedFilesToDelete);
        try {
          await this.deleteFiles(filesToDelete);
        } catch (error) {
          this.logger.warn(
            this.constructor.name,
            'could not delete files from request',
            filesToDelete,
            'does not influence performance - surpress warning',
          );
        }
        await this.preferencesService.removeFilesToDelete(filesToDelete);
      }
      this.isTransmitting$.next(false);
    }
    return didTransmit;
  }

  private scanForRemoteActions(delay: number) {
    if (
      (this.subscription && !this.subscription.closed && !this.isRetrying) ||
      this.isTransmitting$.getValue()
    ) {
      // JB20190326: Do not renew the timer when it is already running (and is not a retry timer)
      this.logger.log(this.constructor.name, '- Timer already running');
    } else {
      this.timer$ = timer(delay);
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      this.subscription = this.timer$.subscribe(async (res) => {
        try {
          this.logger.log(
            this.constructor.name,
            '- Beginn scanning for remote actions to process',
          );
          await this.triggerTransmission();
          this.logger.log(
            this.constructor.name,
            '- Finished scanning for remote actions to process',
          );
        } catch (error) {
          this.logger.error(
            this.constructor.name,
            '- Error scanning remote actions',
            error?.message,
          );
          const retryDelay = this.getRetryDelay();
          this.logger.warn(
            this.constructor.name,
            '- Scheduling a retry after ',
            retryDelay,
          );
          this.isRetrying = true;
          this.scanForRemoteActions(retryDelay);
        }
      });
      this.logger.log(
        this.constructor.name,
        '- Subscribed to new timer with delay',
        delay,
      );
    }
  }

  private getRetryDelay(): number {
    const delay = TIMES.RETRY * this.retryMultiplier;
    this.retryMultiplier *= 2;
    return Math.min(delay, TIMES.MAX_INTERVAL);
  }

  private async timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Runs a remote action's afterSendActions once it was transmitted successfully.
   *
   * Shared by both transmission paths - the instant one in sendRemoteAction() for
   * non-cachable actions, and the queued one in transmit(). The block used to be
   * duplicated in both, which made it impossible to tell from the log which one
   * had actually run.
   *
   * On a successful response every response key is merged into the action's
   * params, so a handler can read the backend's answer (e.g. the AC infos of a
   * dynform upload). Note this OVERWRITES params of the same name - the backend
   * uses lowercase keys, so params set by a builder should be camelCase.
   */
  private async executeAfterSendActions(
    remoteAction: RemoteAction,
    response: any,
  ): Promise<void> {
    this.logger.log(
      this.constructor.name,
      'Execute afterSendActions',
      remoteAction.afterSendActions,
    );

    if (
      remoteAction.afterSendActions == undefined ||
      remoteAction.afterSendActions.length === 0
    ) {
      return;
    }

    for (const afterSendAction of remoteAction.afterSendActions) {
      if (response?.['procstate'] == 'OK') {
        if (afterSendAction.params == undefined) {
          afterSendAction.params = {};
        }
        for (const key of Object.keys(response)) {
          afterSendAction.params[key] = response[key];
        }
      }
      console.log("JBAC2 execute afterSendAction", response?.['procstate'], afterSendAction.target, response, afterSendAction.params)
      await this.executeActionFn(afterSendAction);
    }
  }

  private postSubmitExecution(remoteAction: RemoteAction) {
    // Clear unnecessary big fields of a transmitted remoteAction
    if (remoteAction.data) {
      // Photodoc & DynForm Screenshot
      remoteAction.data['image'] = undefined;

      // DynForm Data
      remoteAction.data['options'] = undefined;
    }

    return remoteAction;
  }

  private async transmitRemoteActionBatchFromBackground(
    finishedCall: () => Promise<{
      finished: boolean;
      furtherExecute?: boolean;
    }>,
  ) {
    try {
      await this.processRemoteActions();
      this.logger.log(
        this.constructor.name,
        'JBackground - Process remote actions successful - notify Background Fetch Plugin',
      );
      let result = await finishedCall();

      if (result.finished) {
        this.logger.log(
          this.constructor.name,
          'JBackground - Everything transmitted in background - no more background execution necessary',
        );
        if (this.waitForUI !== undefined) {
          this.waitForUI.resolve(true);
        }
        return;
      }

      if (result?.furtherExecute === true) {
        this.logger.log(
          this.constructor.name,
          'JBackground - Plugin requests to immediatly transmit another remote action batch',
        );
        await this.transmitRemoteActionBatchFromBackground(finishedCall);
      } else {
        this.logger.log(
          this.constructor.name,
          'JBackground - Still some remote actions missing to transmit - but Plugin does not want to transmit more remote actions at the moment',
        );
        if (this.waitForUI !== undefined) {
          this.waitForUI.resolve(true);
        }
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'JBackground - Process remote actions failed',
        error,
      );
      if (this.waitForUI !== undefined) {
        this.waitForUI.reject(error);
      }
    }
  }

  private async triggerTransmission() {
    if (!this.isTransmitting$.getValue()) {
      if (Capacitor.isNativePlatform()) {
        await LBSBackgroundFetch.triggerTransmission();
        // if (Capacitor.isNativePlatform()) {
          this.waitForUI = new Deferred();
          await this.waitForUI.promise;
          this.waitForUI = undefined;
        // }
      } else {
        await this.transmitRemoteActionBatchFromBackground(() => {
          return LBSBackgroundFetch.finishedBackgroundProcessingRemoteActionBatchTransmission(
            {
              remainingRemoteActions: this.remoteActions$.getValue()?.length ?? 0,
            },
          );
        });
      }
    } else {
      this.logger.log(
        this.constructor.name,
        "- Couldn't trigger Background Transmission as a transmission is already in Progress",
      );
    }
  }

  async initializeCompleted(): Promise<void> {}

  private async sendLocalNotificationForRemainingRar(numberOfRemainingRAR: number) {
    if (numberOfRemainingRAR == undefined || numberOfRemainingRAR == 0) {
      return;
    }

    const permStatus = await LocalNotifications.checkPermissions();
    if (permStatus.display != "granted") {
      return;
    }

    const notificationTitle = this.translate.instant(
      `SERVICE.CACHED-FIFO-REMOTE-ACTION.LOCAL-NOTIFICATIONS.FAILED-SENDING-REMOTE-ACTIONS.TITLE`,
    );
    const notificationBody = this.translate.instant(
      `SERVICE.CACHED-FIFO-REMOTE-ACTION.LOCAL-NOTIFICATIONS.FAILED-SENDING-REMOTE-ACTIONS.BODY`,
      {
        probablyStuckMessages: numberOfRemainingRAR,
      },
    );

    let scheduleOptions: ScheduleOptions = {
      notifications: [
        {
          title: notificationTitle,
          body: notificationBody,
          id: LBSUtils.randomIntFromInterval(-2147483648, 2147483647),
          smallIcon: 'notification_icon',
          largeIcon: 'notification_icon',
          autoCancel: true,
          actionTypeId: 'testActionTypeid',
        },
      ],
    };
    LocalNotifications.schedule(scheduleOptions)
      .then((result) => {
        this.logger.log(
          this.constructor.name,
          'Probably Stuck Remote Actions Local Notifications scheduled:',
          result,
        );
      })
      .catch((err) => {
        this.logger.log(
          this.constructor.name,
          'Probably Stuck Remote Actions Local Notifications scheduled failed',
          err,
        );
      });
  }
}
